
import {
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { isNil } from "lodash";
import { combineLatest, Observable, ReplaySubject, Subject } from "rxjs";
import { filter, shareReplay, takeUntil, tap } from "rxjs/operators";
import { CertifikatTyper, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { RuleErrorContext, RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KontraktCertifikat, KontraktCertifikatDTO, KontraktCertifikatEditDTO, VKCertifikatDTO } from "~app/features/virkeskop/models/certifiering-models";
import { VirkeskostnaderEvent } from "~app/features/virkeskop/models/virkeskostnader/virkeskostnader-models";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { LeveransvirkesplanMediatorService } from "~app/features/virkeskop/services/leveransvirkesplan-mediator.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { VirkeskostnaderMediatorService } from "~app/features/virkeskop/services/virkeskostnader-mediator.service";
import { VirkeskopValidationMessageComponent } from "../../../validation/validation-message/virkeskop-validation-message.component";
import { KontraktCertifikatComponent } from "../kontrakt-certifikat/kontrakt-certifikat.component";

@Component({
    selector: "rask-kontrakt-certifiering",
    templateUrl: "./kontrakt-certifiering.component.html",
    styleUrls: ["./kontrakt-certifiering.component.scss"],
    providers: [LeveransvirkesplanMediatorService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        VirkeskopValidationMessageComponent,
        KontraktCertifikatComponent
    ]
})
export class KontraktCertifieringComponent implements OnInit, OnDestroy {

    @Input()
    set kontrakt(value: EntityDTO) {
        this._kontrakt = value;
        this.kontraktChanged();
    }

    get kontrakt() {
        return this._kontrakt;
    }

    protected certifikatListObservable: Observable<VKCertifikatDTO[]>;
    protected certifikatList: KontraktCertifikatEditDTO[] = [];
    protected initialized = false;

    private readonly certifikatListSubject$ = new ReplaySubject<KontraktCertifikatEditDTO[]>(1);
    private readonly ngUnsubscribe$ = new Subject<void>();

    private _kontrakt: EntityDTO;
    private readonly initialCertList: KontraktCertifikatDTO[] = CertifikatTyper.alla.map(c => ({
        certifikatstyp: c,
        selected: false
    }));

    private readonly avtalService = inject(AvtalService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);

    ngOnInit(): void {
        this.certifikatListObservable = this.avtalService.getCertifikat().pipe(
            shareReplay(1)
        );

        combineLatest([
            this.virkeskostnaderMediatorService.virkeskostnader$,
            this.certifikatListSubject$
        ])
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(([
                    virkeskostnaderEvent,
                    certifikat
                ]) => certifikat?.length > 0 && ObjectUtils.hasValue(virkeskostnaderEvent)),
                tap(([
                    virkeskostnaderEvent,
                    certifikat
                ]) => this.checkCertifieratHandelssortiment(virkeskostnaderEvent, certifikat))
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    protected get validationIcon(): string {
        return this.validationService.getEntitySeverity("certifikat", this.kontrakt.guid);
    }

    protected updateKontraktCertifikat(certifikat: KontraktCertifikat) {
        const cert = this.certifikatList.find(c => c.certifikatstyp === certifikat.certifikatstyp);
        cert.certifikatId = certifikat.certifikatId;
        cert.certifikatstyp = certifikat.certifikatstyp;
        cert.kontrolldatum = certifikat.kontrolldatum;
        cert.selected = certifikat.selected;
        this.kontraktMediatorService.updateKontraktCertifikat(this.certifikatList);
        this.certifikatListSubject$.next(this.certifikatList);
    }

    protected async checkCertifieratHandelssortiment(virkeskostnaderEvent: VirkeskostnaderEvent, certifikat: KontraktCertifikatEditDTO[]): Promise<void> {
        const certifikatExist = certifikat.some(c => c.selected === true && [
            CertifikatTyper.Fsc,
            CertifikatTyper.Pefc
        ].includes(c.certifikatstyp));
        const OcertifieratHandelssortimentNamn = virkeskostnaderEvent.virkeskostnader.getOcertifieratHandelssortimentNamn();

        if (!isNil(OcertifieratHandelssortimentNamn) && certifikatExist) {
            this.validationService.updateClientSideValidation(true, {
                entityGuids: [this.kontraktMediatorService.kontrakt.guid],
                message: `Handelssortiment ${OcertifieratHandelssortimentNamn} får inte väljas i kombination med certifiering.`,
                messageId: "OCertHandel",
                severity: RuleSeverityLevel.warning,
                strategy: RuleVisibilityStrategy.alwaysShow,
                context: RuleErrorContext.save,
                entityType: "certifikat"
            });
        }
    }

    private kontraktChanged() {
        if (this.kontrakt?.id) {
            this.avtalService.getCertifikatForKontrakt(this.kontrakt.id)
                .subscribe(dto => {
                    this.initModel(dto);
                });
        } else {
            this.initModel(this.initialCertList);
        }
    }

    private initModel(dtoList?: KontraktCertifikatDTO[]) {
        this.certifikatList = dtoList.map(c => ({
            certifikatstyp: c.certifikatstyp,
            certifikatId: c.id,
            kontrolldatum: c.kontrolldatum,
            certifikatTomDatum: c.certifikatTomDatum,
            certifikatsnummer: c.certifikatsnummer,
            selected: c.selected
        }));

        this.certifikatListSubject$.next(this.certifikatList);
        this.initialized = true;
        this.validateCertifikat();
    }

    private validateCertifikat() {
        const shouldValidate = !this.kontraktMediatorService.kontrakt.vacsKontraktAvslutat &&
            (this.kontraktMediatorService.kontrakt.status === VirkeskopKontraktstatus.Kontrakt &&
                new Date(this.kontraktMediatorService.kontrakt.kontraktGiltigtTom) >= DateUtils.now()) ||
            (this.kontraktMediatorService.kontrakt.status === VirkeskopKontraktstatus.Anbud &&
                new Date(this.kontraktMediatorService.kontrakt.anbudGiltighetTom) >= DateUtils.now());

        if (!shouldValidate) {
            return;
        }

        const fscCertifikat = this.certifikatList.find(c => c.certifikatstyp === CertifikatTyper.Fsc);
        const pefcCertifikat = this.certifikatList.find(c => c.certifikatstyp === CertifikatTyper.Pefc);

        const showFscValidation = ObjectUtils.hasValue(fscCertifikat?.certifikatTomDatum) && new Date(fscCertifikat?.certifikatTomDatum) <= DateUtils.now();
        const showPefcValidation = ObjectUtils.hasValue(pefcCertifikat?.certifikatTomDatum) && new Date(pefcCertifikat?.certifikatTomDatum) <= DateUtils.now();

        this.validationService.updateClientSideValidation(showFscValidation, {
            entityGuids: [this.kontraktMediatorService.kontrakt.guid],
            message: `FSC Certifikat ${fscCertifikat?.certifikatsnummer} har utgått `,
            messageId: "FSCTomDate",
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "certifikat"
        });

        this.validationService.updateClientSideValidation(showPefcValidation, {
            entityGuids: [this.kontraktMediatorService.kontrakt.guid],
            message: `PEFC Certifikat ${pefcCertifikat?.certifikatsnummer} har utgått `,
            messageId: "PefcTomDate",
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "certifikat"
        });
    }
}
